/**
 * Frontend API for trip expense splitting (Costs tab).
 *
 * All amounts cross the wire as INTEGER cents — parse/format happens in
 * src/utils/money.js, never here. Queries return data or null; mutations
 * return { success, ... } (matching the backend service convention), so
 * callers never need try/catch.
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';

async function query(path) {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: AuthService.getJsonHeaders(),
    });
    return await AuthService.handleResponse(response);
  } catch (error) {
    console.error(`tripExpensesApi GET ${path} failed:`, error);
    return null;
  }
}

async function mutate(path, method, body) {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: AuthService.getJsonHeaders(),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    return await AuthService.handleResponse(response);
  } catch (error) {
    console.error(`tripExpensesApi ${method} ${path} failed:`, error);
    return { success: false, error: error.message || 'Request failed' };
  }
}

const tripExpensesApi = {
  /** Full Costs-tab payload (expenses+splits, settlements, balances, totals). */
  getTripCosts(tripId) {
    return query(`/api/trips/${tripId}/expenses`);
  },

  createExpense(tripId, data) {
    return mutate(`/api/trips/${tripId}/expenses`, 'POST', data);
  },

  updateExpense(tripId, expenseId, data) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}`, 'PUT', data);
  },

  deleteExpense(tripId, expenseId) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}`, 'DELETE');
  },

  /** Change history for one expense ({ audit: [...] } or null). */
  getExpenseAudit(tripId, expenseId) {
    return query(`/api/trips/${tripId}/expenses/${expenseId}/audit`);
  },

  /** Attach or replace the expense receipt (dataURL or raw base64). */
  setExpenseReceipt(tripId, expenseId, imageBase64) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}/receipt`, 'PUT', {
      image_base64: imageBase64,
    });
  },

  deleteExpenseReceipt(tripId, expenseId) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}/receipt`, 'DELETE');
  },

  /**
   * Receipts are money-private and served authed, so a plain <img src>
   * can't load them (no JWT on the request) — fetch with auth headers into
   * an object URL instead. Caller owns revokeObjectURL.
   */
  async fetchReceiptObjectUrl(tripId, expenseId, version) {
    try {
      const response = await fetch(
        `${API_BASE}/api/trips/${tripId}/expenses/${expenseId}/receipt?v=${encodeURIComponent(version || '')}`,
        { headers: AuthService.getJsonHeaders() }
      );
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('tripExpensesApi receipt fetch failed:', error);
      return null;
    }
  },

  /** Member-only discussion thread ({ comments: [...] } or null). */
  getExpenseComments(tripId, expenseId) {
    return query(`/api/trips/${tripId}/expenses/${expenseId}/comments`);
  },

  addExpenseComment(tripId, expenseId, commentText) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}/comments`, 'POST', {
      comment_text: commentText,
    });
  },

  deleteExpenseComment(tripId, expenseId, commentId) {
    return mutate(`/api/trips/${tripId}/expenses/${expenseId}/comments/${commentId}`, 'DELETE');
  },

  /**
   * Set (or clear, with null) the trip budget target. MAJOR units in the
   * trip base currency. Member-gated on the expenses router — the trips PUT
   * requires trip EDIT permission, which view-only co-travelers don't have,
   * while the ledger (budget included) is any-accepted-member by design.
   */
  setBudget(tripId, budgetExpected) {
    return mutate(`/api/trips/${tripId}/expenses-budget`, 'PUT', {
      budget_expected: budgetExpected,
    });
  },

  createSettlement(tripId, data) {
    return mutate(`/api/trips/${tripId}/settlements`, 'POST', data);
  },

  deleteSettlement(tripId, settlementId) {
    return mutate(`/api/trips/${tripId}/settlements/${settlementId}`, 'DELETE');
  },

  /**
   * Conversion preview for the add-expense sheet — the same rate the save
   * will lock, so the preview never disagrees with what gets stored.
   * Returns { success, converted_cents, rate, rate_date } or null.
   */
  async fxPreview({ amountCents, from, to, date }) {
    const params = new URLSearchParams({
      amount_cents: String(amountCents),
      from,
      to,
      ...(date ? { date } : {}),
    });
    return query(`/api/fx/preview?${params}`);
  },
};

export default tripExpensesApi;
